import {
  Autocomplete,
  Checkbox,
  CircularProgress,
  TextField,
} from '@mui/material';

import { CheckboxCheckedIcon, CheckboxIcon } from '~/shared';
import type { CampaignSegmentOption, CampaignSegmentsModelView } from '~/types';

import { useGetSegments } from '../hooks';

export type SegmentsSelectProps = {
  id: string;
  label: string;
  value?: CampaignSegmentOption[];
  onChange: (value: CampaignSegmentOption[]) => void;
  segment: keyof CampaignSegmentsModelView;
  multiple?: boolean;
  disabled?: boolean;
};

export function SegmentsSelect({
  id,
  value = [],
  onChange,
  label,
  segment,
  disabled,
}: SegmentsSelectProps) {
  const { isLoading, isError, data } = useGetSegments();

  return (
    <Autocomplete
      multiple
      disableCloseOnSelect
      disabled={disabled || isLoading || isError}
      sx={{ flexGrow: 1, flexBasis: '100%' }}
      id={`segment-selections${id ? `_${id}` : ''}`}
      options={data?.[segment] ?? []}
      getOptionLabel={option => option.name}
      // eslint-disable-next-line @typescript-eslint/no-shadow
      isOptionEqualToValue={(option, value) => option.id === value.id}
      value={value}
      onChange={(_, newValue) => onChange(newValue)}
      renderOption={(props, option) => {
        const isChecked = value.some(val => val.id === option.id);
        return (
          <li {...props}>
            <Checkbox
              icon={CheckboxIcon}
              checkedIcon={CheckboxCheckedIcon}
              style={{ marginRight: 8 }}
              checked={isChecked}
            />
            {option.name}
          </li>
        );
      }}
      renderInput={params => (
        <TextField
          {...params}
          variant="filled"
          label={label}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {isLoading ? (
                  <CircularProgress
                    color="inherit"
                    size={25}
                    sx={{ mt: '-18px' }}
                  />
                ) : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
